/**
 * Booking Service
 * Handles all booking-related Firestore operations
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';

import { db } from './config';
import type { Booking } from '@shared/types/booking';
import { BookingStatus } from '@shared/types/booking';

/**
 * Create a new booking
 */
export async function createBooking(bookingData: {
  userId: string;
  bikeId: string;
  locationId: string;
  startTime: Date;
  endTime: Date;
  rentalType: 'half-day' | 'full-day' | 'multi-day';
  numberOfDays?: number;
  halfDayRate: number;
  fullDayRate: number;
  appliedDiscount?: number;
  totalPrice: number;
  insuranceIncluded: boolean;
  insurancePrice?: number;
  insuranceProvider?: 'oyster' | 'other';
  insurancePolicyId?: string;
  insuranceQuoteId?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
}): Promise<string> {
  const bookingRef = await addDoc(collection(db, 'bookings'), {
    ...bookingData,
    status: 'pending' as BookingStatus,
    startTime: Timestamp.fromDate(bookingData.startTime),
    endTime: Timestamp.fromDate(bookingData.endTime),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Create status history entry
  await addDoc(collection(db, 'bookings', bookingRef.id, 'status_history'), {
    status: 'pending',
    changedAt: serverTimestamp(),
    changedBy: bookingData.userId,
  });

  return bookingRef.id;
}

/**
 * Get booking by ID
 */
export async function getBooking(bookingId: string): Promise<Booking | null> {
  const bookingDoc = await getDoc(doc(db, 'bookings', bookingId));

  if (!bookingDoc.exists()) {
    return null;
  }

  const data = bookingDoc.data();
  return {
    id: bookingDoc.id,
    ...data,
    startTime: (data.startTime as Timestamp)?.toDate() || new Date(),
    endTime: (data.endTime as Timestamp)?.toDate() || new Date(),
    checkInTime: data.checkInTime ? (data.checkInTime as Timestamp).toDate() : undefined,
    checkOutTime: data.checkOutTime ? (data.checkOutTime as Timestamp).toDate() : undefined,
    createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
    updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
  } as Booking;
}

/**
 * Get bookings for a user
 */
export async function getUserBookings(userId: string): Promise<Booking[]> {
  const bookingsQuery = query(
    collection(db, 'bookings'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(bookingsQuery);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startTime: (data.startTime as Timestamp)?.toDate() || new Date(),
      endTime: (data.endTime as Timestamp)?.toDate() || new Date(),
      checkInTime: data.checkInTime ? (data.checkInTime as Timestamp).toDate() : undefined,
      checkOutTime: data.checkOutTime ? (data.checkOutTime as Timestamp).toDate() : undefined,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as Booking;
  });
}

/**
 * Get bookings for a bike
 */
export async function getBikeBookings(
  bikeId: string,
  startDate?: Date,
  endDate?: Date
): Promise<Booking[]> {
  let bookingsQuery = query(
    collection(db, 'bookings'),
    where('bikeId', '==', bikeId),
    where('status', 'in', ['pending', 'confirmed', 'active'])
  );

  if (startDate && endDate) {
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);
    bookingsQuery = query(
      collection(db, 'bookings'),
      where('bikeId', '==', bikeId),
      where('status', 'in', ['pending', 'confirmed', 'active']),
      where('startTime', '>=', startTimestamp),
      where('endTime', '<=', endTimestamp)
    );
  }

  const snapshot = await getDocs(bookingsQuery);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startTime: (data.startTime as Timestamp)?.toDate() || new Date(),
      endTime: (data.endTime as Timestamp)?.toDate() || new Date(),
      checkInTime: data.checkInTime ? (data.checkInTime as Timestamp).toDate() : undefined,
      checkOutTime: data.checkOutTime ? (data.checkOutTime as Timestamp).toDate() : undefined,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as Booking;
  });
}

/**
 * Get bookings for a location
 */
export async function getLocationBookings(locationId: string): Promise<Booking[]> {
  const bookingsQuery = query(
    collection(db, 'bookings'),
    where('locationId', '==', locationId),
    orderBy('startTime', 'asc')
  );

  const snapshot = await getDocs(bookingsQuery);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      startTime: (data.startTime as Timestamp)?.toDate() || new Date(),
      endTime: (data.endTime as Timestamp)?.toDate() || new Date(),
      checkInTime: data.checkInTime ? (data.checkInTime as Timestamp).toDate() : undefined,
      checkOutTime: data.checkOutTime ? (data.checkOutTime as Timestamp).toDate() : undefined,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
    } as Booking;
  });
}

/**
 * Check if a bike is available for a time period
 */
export async function checkBikeAvailability(
  bikeId: string,
  startTime: Date,
  endTime: Date,
  excludeBookingId?: string
): Promise<{ available: boolean; conflictingBookings?: Booking[] }> {
  // Query for overlapping bookings
  const overlappingQuery = query(
    collection(db, 'bookings'),
    where('bikeId', '==', bikeId),
    where('status', 'in', ['pending', 'confirmed', 'active'])
  );

  const snapshot = await getDocs(overlappingQuery);
  const conflictingBookings: Booking[] = [];

  snapshot.docs.forEach((doc) => {
    const bookingId = doc.id;
    if (excludeBookingId && bookingId === excludeBookingId) {
      return; // Skip the booking we're checking against (for updates)
    }

    const data = doc.data();
    const bookingStart = (data.startTime as Timestamp).toDate();
    const bookingEnd = (data.endTime as Timestamp).toDate();

    // Check for overlap: new booking overlaps if it starts before existing ends AND ends after existing starts
    if (startTime < bookingEnd && endTime > bookingStart) {
      conflictingBookings.push({
        id: bookingId,
        ...data,
        startTime: bookingStart,
        endTime: bookingEnd,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      } as Booking);
    }
  });

  return {
    available: conflictingBookings.length === 0,
    conflictingBookings: conflictingBookings.length > 0 ? conflictingBookings : undefined,
  };
}

/**
 * Update booking status
 */
export async function updateBookingStatus(
  bookingId: string,
  status: BookingStatus,
  updatedBy: string,
  notes?: string
): Promise<void> {
  const bookingRef = doc(db, 'bookings', bookingId);
  const updates: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
  };

  if (notes) {
    updates.notes = notes;
  }

  // Set check-in/check-out times based on status
  if (status === 'active') {
    updates.checkInTime = serverTimestamp();
  } else if (status === 'completed') {
    updates.checkOutTime = serverTimestamp();
  }

  await updateDoc(bookingRef, updates);

  // Add to status history
  await addDoc(collection(db, 'bookings', bookingId, 'status_history'), {
    status,
    changedAt: serverTimestamp(),
    changedBy: updatedBy,
    notes,
  });
}

/**
 * Cancel a booking
 */
export async function cancelBooking(
  bookingId: string,
  cancelledBy: string,
  reason?: string
): Promise<void> {
  await updateBookingStatus(bookingId, BookingStatus.CANCELLED, cancelledBy, reason || 'Booking cancelled');
}

/**
 * Update booking payment info
 */
export async function updateBookingPayment(
  bookingId: string,
  paymentId: string
): Promise<void> {
  await updateDoc(doc(db, 'bookings', bookingId), {
    paymentId,
    status: 'confirmed' as BookingStatus,
    updatedAt: serverTimestamp(),
  });

  // Update status history
  await addDoc(collection(db, 'bookings', bookingId, 'status_history'), {
    status: 'confirmed',
    changedAt: serverTimestamp(),
    changedBy: 'system',
    notes: 'Payment confirmed',
  });
}

/**
 * Update booking QR code
 */
export async function updateBookingQRCode(bookingId: string, qrCode: string): Promise<void> {
  await updateDoc(doc(db, 'bookings', bookingId), {
    qrCode,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Generate and save QR code for a booking
 */
export async function generateAndSaveQRCode(bookingId: string, userId: string): Promise<string> {
  const { generateBookingQRCodeURL } = await import('@lib/qrcode');
  const qrCodeUrl = generateBookingQRCodeURL(bookingId, userId);
  await updateBookingQRCode(bookingId, qrCodeUrl);
  return qrCodeUrl;
}

/**
 * Get available bikes at a location for a time period
 */
export async function getAvailableBikes(
  locationId: string,
  startTime: Date,
  endTime: Date
): Promise<string[]> {
  // Get all bikes at location
  const bikesQuery = query(
    collection(db, 'bikes'),
    where('locationId', '==', locationId),
    where('status', '==', 'available')
  );

  const bikesSnapshot = await getDocs(bikesQuery);
  const allBikeIds = bikesSnapshot.docs.map((doc) => doc.id);

  // Check availability for each bike
  const availableBikeIds: string[] = [];

  for (const bikeId of allBikeIds) {
    const availability = await checkBikeAvailability(bikeId, startTime, endTime);
    if (availability.available) {
      availableBikeIds.push(bikeId);
    }
  }

  return availableBikeIds;
}

